import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import BasicButton from "../basics/BasicButton";
import {
  type NotificationLogEntry,
  useGetSchedulerStatusQuery,
  useLazyGetNotificationLogsQuery,
  useToggleSchedulerTaskMutation,
} from "../../services/rtkApi/schedulerAdminApi";
import { useAlert } from "../../ui/components/ConfirmDialog";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #c9febe;
  align-items: stretch;
  padding: 20px;
  min-height: 100vh;
  max-width: 960px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid #e5e5e5;
  }
  th {
    font-weight: 600;
    font-size: 0.85rem;
    color: #333;
  }
`;

const WarningBanner = styled.div`
  background: #fff8e6;
  border: 1px solid #e8c96a;
  color: #5c4a16;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 12px;
`;

const LogScroll = styled.div`
  max-height: 480px;
  overflow: auto;
`;

const CHANNEL_OPTIONS = [
  { value: "all", label: "All channels" },
  { value: "notification", label: "notification" },
  { value: "broadcast", label: "broadcast" },
  { value: "action", label: "action" },
  { value: "email", label: "email" },
  { value: "channel_announcement", label: "channel_announcement" },
  { value: "tg_logger", label: "tg_logger" },
];

function normalizeLogEntry(raw: NotificationLogEntry | Record<string, unknown>): NotificationLogEntry {
  const ts = raw.timestamp;
  const timestamp =
    typeof ts === "string"
      ? ts
      : ts instanceof Date
        ? ts.toISOString()
        : typeof ts === "object" && ts != null && "$date" in ts
          ? String((ts as { $date: string }).$date)
          : new Date(ts as string).toISOString();
  return {
    _id: String(raw._id),
    timestamp,
    channel: String(raw.channel),
    recipient: String(raw.recipient),
    message: String(raw.message),
    status: String(raw.status),
    statusDetail: raw.statusDetail != null ? String(raw.statusDetail) : undefined,
    source: raw.source != null ? String(raw.source) : undefined,
    metadata: raw.metadata as Record<string, unknown> | undefined,
  };
}

function statusStyle(status: string): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: "0.75rem",
    fontWeight: 600,
  };
  if (status === "sent") return { ...base, background: "#d4edda", color: "#155724" };
  if (status === "skipped") return { ...base, background: "#fff3cd", color: "#856404" };
  if (status === "failed") return { ...base, background: "#f8d7da", color: "#721c24" };
  return { ...base, background: "#e9ecef", color: "#333" };
}

const SchedulerAdmin: React.FC = () => {
  const alert = useAlert();
  const { data, isLoading, error, refetch } = useGetSchedulerStatusQuery();
  const [toggleTask, { isLoading: toggling }] = useToggleSchedulerTaskMutation();
  const [fetchLogs, { isFetching: logsFetching }] = useLazyGetNotificationLogsQuery();

  const [logChannel, setLogChannel] = useState("all");
  const [logTaskDraft, setLogTaskDraft] = useState("");
  const [logTaskApplied, setLogTaskApplied] = useState("");
  const [notificationLogs, setNotificationLogs] = useState<NotificationLogEntry[]>([]);

  const channelParam = logChannel === "all" ? undefined : logChannel;
  const taskParam = logTaskApplied.trim() || undefined;

  const loadLogsFirstPage = useCallback(async () => {
    const res = await fetchLogs({
      limit: 50,
      channel: channelParam,
      task: taskParam,
    }).unwrap();
    setNotificationLogs(res.logs.map(normalizeLogEntry));
  }, [fetchLogs, channelParam, taskParam]);

  useEffect(() => {
    void loadLogsFirstPage();
  }, [loadLogsFirstPage]);

  const loadMoreLogs = async () => {
    const last = notificationLogs[notificationLogs.length - 1];
    if (!last) return;
    const res = await fetchLogs({
      limit: 50,
      before: last.timestamp,
      channel: channelParam,
      task: taskParam,
    }).unwrap();
    const next = res.logs.map(normalizeLogEntry);
    setNotificationLogs((prev) => {
      const ids = new Set(prev.map((l) => l._id));
      return [...prev, ...next.filter((l) => !ids.has(l._id))];
    });
  };

  const errMessage =
    error && "data" in error && error.data && typeof error.data === "object" && "error" in error.data
      ? String((error.data as { error?: string }).error)
      : error
        ? "Could not load scheduler status."
        : null;

  const handleToggle = async (taskName: string, enabled: boolean) => {
    try {
      await toggleTask({ taskName, enabled }).unwrap();
    } catch (e) {
      console.error("toggleSchedulerTask", e);
      await alert({ title: "Update failed", message: "Failed to update task" });
    }
  };

  return (
    <PageContainer className="sched-page">
      <style>{`
        @media (max-width: 700px) {
          .sched-page { padding: 12px 8px !important; }
          .sched-row { flex-wrap: wrap !important; gap: 8px !important; }
          .sched-input-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
          .sched-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 0 -8px; }
        }
      `}</style>
      <div className="sched-row flex flex-wrap gap-3 items-center">
        <Link to="/admin">
          <BasicButton text="Admin hub" />
        </Link>
        <Link to="/admin/services">
          <BasicButton text="Services" />
        </Link>
        <BasicButton
          text="Refresh"
          onClick={() => {
            void refetch();
            void loadLogsFirstPage();
          }}
        />
      </div>

      <h1 className="text-2xl font-semibold mt-6 mb-2">Scheduler tasks</h1>
      <p className="text-sm text-gray-700 max-w-2xl">
        Task on/off is stored in MongoDB and applies immediately when{" "}
        <code className="bg-gray-100 px-1 rounded">SCHEDULER_ENABLED=true</code> on the backend.
        Env vars like <code className="bg-gray-100 px-1 rounded">AOTD_NOTIFICATIONS_ENABLED</code>{" "}
        only seed defaults when a task has no row yet.
      </p>

      {data && !data.schedulerGloballyEnabled && (
        <WarningBanner>
          <strong>Scheduler globally off.</strong> Set <code>SCHEDULER_ENABLED=true</code> on the
          backend and restart so cron jobs run. You can still edit toggles below; they take effect
          on the next start.
        </WarningBanner>
      )}

      {data?.schedulerGloballyEnabled && !data.schedulerRunning && (
        <WarningBanner>
          <strong>Cron not running</strong> (backend may still be starting, or initialization
          failed). Check backend logs.
        </WarningBanner>
      )}

      {errMessage && (
        <WarningBanner style={{ background: "#ffecec", borderColor: "#e8a0a0", color: "#7a1f1f" }}>
          {errMessage}
        </WarningBanner>
      )}

      <Card>
        {isLoading && <p>Loading…</p>}
        {!isLoading && data?.tasks && (
          <div className="sched-table-wrap">
            <Table>
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Cron</th>
                  <th>Enabled (DB)</th>
                  <th>Running</th>
                </tr>
              </thead>
              <tbody>
                {data.tasks.map((t) => (
                  <tr key={t.name}>
                    <td>
                      <code>{t.name}</code>
                    </td>
                    <td>
                      <code style={{ fontSize: "0.8rem" }}>{t.cron}</code>
                    </td>
                    <td>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={t.enabled}
                          disabled={toggling}
                          onChange={(e) => {
                            void handleToggle(t.name, e.target.checked);
                          }}
                        />
                        <span>{t.enabled ? "on" : "off"}</span>
                      </label>
                    </td>
                    <td>{t.running ? "yes" : "no"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <h2 className="text-xl font-semibold mt-8 mb-2">Notification audit log</h2>
      <p className="text-sm text-gray-700 max-w-2xl mb-2">
        Outbound messages via messenger and Telegram channel/logger (TTL 30 days in MongoDB).
      </p>

      <Card>
        <div className="sched-row flex flex-wrap gap-3 items-end mb-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-600">Channel</span>
            <select
              className="sched-input-full border rounded px-2 py-1 bg-white"
              value={logChannel}
              onChange={(e) => setLogChannel(e.target.value)}
            >
              {CHANNEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm flex-1 min-w-[200px]">
            <span className="text-gray-600">Source contains</span>
            <input
              type="text"
              className="sched-input-full border rounded px-2 py-1"
              placeholder="e.g. aotd_reminder, telegram_bot"
              value={logTaskDraft}
              onChange={(e) => setLogTaskDraft(e.target.value)}
            />
          </label>
          <BasicButton
            text="Apply filters"
            onClick={() => {
              setLogTaskApplied(logTaskDraft);
            }}
            disabled={logsFetching}
          />
        </div>

        {logsFetching && notificationLogs.length === 0 && <p>Loading logs…</p>}

        <LogScroll>
          <div className="sched-table-wrap">
            <Table>
              <thead>
                <tr>
                  <th>Time (UTC)</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Recipient</th>
                  <th>Source</th>
                  <th>Message</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {notificationLogs.map((row) => (
                  <tr key={row._id}>
                    <td style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                      {row.timestamp.replace("T", " ").slice(0, 19)}
                    </td>
                    <td>
                      <code style={{ fontSize: "0.75rem" }}>{row.channel}</code>
                    </td>
                    <td>
                      <span style={statusStyle(row.status)}>{row.status}</span>
                    </td>
                    <td>
                      <code style={{ fontSize: "0.75rem" }}>{row.recipient}</code>
                    </td>
                    <td>
                      <code style={{ fontSize: "0.75rem" }}>{row.source ?? "—"}</code>
                    </td>
                    <td style={{ fontSize: "0.8rem", maxWidth: 280, wordBreak: "break-word" }}>
                      {row.message}
                    </td>
                    <td style={{ fontSize: "0.75rem", maxWidth: 200, wordBreak: "break-word" }}>
                      {row.statusDetail ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </LogScroll>

        {notificationLogs.length > 0 && (
          <div className="mt-3 flex gap-2">
            <BasicButton text="Load more" onClick={() => void loadMoreLogs()} disabled={logsFetching} />
          </div>
        )}
        {notificationLogs.length === 0 && !logsFetching && (
          <p className="text-sm text-gray-500">No log entries match the current filters.</p>
        )}
      </Card>
    </PageContainer>
  );
};

export default SchedulerAdmin;
